/*

  Buttons.ts

  Buttons below rack component

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjálmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { ts } from "./i18n";
import { AnimatedNetskraflLogo } from "./logo";
import { Component, m, VnodeAttrs, VnodeChildren } from "./mithril";
import { IBaseGame, IView } from "./types";
import { buttonOut, buttonOver, glyph, nbsp } from "./util";

export function makeButton(
  cls: string, disabled: boolean, onclick: () => void,
  title?: string, children?: VnodeChildren, visible?: boolean, id?: string
): VnodeChildren {
  // Create a button element, wrapping the disabling logic
  // and other boilerplate
  const attrs: VnodeAttrs = {
    onmouseout: buttonOut,
    onmouseover: buttonOver,
    style: { visibility: visible === false ? "hidden" : "visible" },
  };
  if (title)
    attrs.title = title;
  if (id)
    attrs.id = id;
  if (disabled)
    attrs.onclick = (ev: Event) => ev.preventDefault();
  else
    attrs.onclick = (ev: Event) => {
      onclick && onclick();
      ev.preventDefault();
    };
  return m(
    "." + cls + (disabled ? ".disabled" : ""),
    attrs, children // children may be omitted
  );
}

const Score: Component<{ game: IBaseGame }> = {
  view: (vnode) => {
    // Shows the score of the current word
    const { game } = vnode.attrs;
    let sc = [".score"];
    if (game.manual)
      sc.push("manual");
    else
      if (game.wordGood) {
        sc.push("word-good");
        if (game.currentScore !== undefined && game.currentScore >= 50)
          sc.push("word-great");
      }
    let txt = (game.currentScore === undefined ? "?" : game.currentScore.toString())
    return m(sc.join("."), { title: txt }, txt);
  }
};

interface IRecallScrambleAttributes {
  view: IView;
  game: IBaseGame;
  visible?: boolean;
  disabled?: boolean;
}

const RecallButton: Component<IRecallScrambleAttributes> = {
  view: (vnode) => {
    // Create a tile recall button
    const { view, game, disabled, visible = true } = vnode.attrs;
    if (!game) return undefined;
    return makeButton(
      "recallbtn", !!disabled,
      () => { game.resetRack(); view.updateScale(); },
      ts("Færa stafi aftur í rekka"),
      glyph("down-arrow"),
      visible,
    );
  }
};

const ScrambleButton: Component<IRecallScrambleAttributes> = {
  view: (vnode) => {
    // Create a tile scramble button
    const { game, disabled, visible = true } = vnode.attrs;
    if (!game) return undefined;
    return makeButton(
      "scramblebtn", !!disabled,
      () => game.rescrambleRack(), // Note: plain game.rescrambleRack doesn't work here
      ts("Stokka upp rekka"),
      glyph("random"),
      visible,
    );
  }
};

interface IAttributes {
  view: IView;
  game: IBaseGame;
}

export const ButtonsRecallScramble: Component<IAttributes> = {
  view: (vnode) => {
    // The button to the left of the rack in the mobile UI,
    // which is either a 'Recall tiles' or a 'Scramble rack' button
    // depending on whether any tiles have been placed on the board
    // or not.
    const { view, game } = vnode.attrs;
    if (!game) return undefined;
    const s = game.buttonState();
    if (s.showRecall && !s.showingDialog)
      // Show a 'Recall tiles' button
      return m(RecallButton, { view, game });
    const visible = s.showScramble && !s.tilesPlaced;
    const disabled = s.showingDialog;
    return m(ScrambleButton, { view, game, visible, disabled });
  }
};

export const Buttons: Component<{ view: IView }> = {
  view: (vnode) => {
    // The set of buttons below the game board, alongside the rack
    // (fullscreen view) or below the rack (mobile view)
    const { view } = vnode.attrs;
    const { game } = view.model; // Netskrafl only
    let r: VnodeChildren = [];
    if (!game) return r;
    const s = game.buttonState();
    r.push(m(".word-check" +
      (s.wordGood ? ".word-good" : "") +
      (s.wordBad ? ".word-bad" : "")));
    if (s.showChallenge) {
      // Show a button that allows the player to challenge the opponent's
      // last move
      const disabled = (s.tilesPlaced || s.showingDialog) && !s.lastChallenge;
      r.push(
        makeButton(
          "challenge", disabled,
          () => game.submitChallenge(), // Note: plain game.submitChallenge doesn't work here
          'Véfenging (röng kostar 10 stig)',
          glyph("ban-circle"),
        )
      );
    }
    if (s.showRecall) {
      // Show button to recall tiles from the board into the rack
      r.push(m(RecallButton, { view, game, disabled: s.showingDialog }));
    }
    if (s.showScramble) {
      // Show button to scramble (randomly reorder) the rack tiles
      r.push(m(ScrambleButton, { view, game, disabled: s.showingDialog }));
    }
    if (s.showMove) {
      // "Plain" move button for fullscreen
      const submit_move = ts("submit_move"); // 'Move' or 'Leika'
      const disabled = !s.tilesPlaced || s.showingDialog;
      r.push(
        makeButton(
          "submitmove", disabled,
          () => { game.submitMove(); view.updateScale(); },
          submit_move,
          [submit_move, glyph("play")],
        )
      );
    }
    if (s.showMoveMobile) {
      // Submit-Move button on mobile, which also shows the score
      // and whether the move is good or bad
      let classes: string[] = ["submitmove"];
      let wordIsPlayable = game.currentScore !== undefined;
      if (game.manual) {
        classes.push("manual")
      } else if (s.wordGood) {
        classes.push("word-good");
        if (game.currentScore !== undefined && game.currentScore >= 50)
          classes.push("word-great");
      } else if (s.wordBad) {
        classes.push("word-bad");
        wordIsPlayable = false;
      }
      const text = (game.currentScore === undefined) ? "?" : game.currentScore.toString();
      let legend: VnodeChildren[] = [m("span.score-mobile", text)];
      if (s.canPlay && wordIsPlayable)
        legend.push(glyph("play"));
      else
        legend.push(glyph("remove"));
      let action: () => void;
      if (s.canPlay) {
        if (wordIsPlayable)
          action = () => { game.submitMove(); view.updateScale(); };
        else
          action = () => { /* TODO: Add some kind of feedback? */ };
      }
      else {
        action = () => {
          // Make the 'opp-turn' flash, to remind the user that it's not her turn
          const el = document.querySelector("div.opp-turn") as HTMLElement;
          if (el) {
            el.classList.toggle("flashing", true);
            setTimeout(() => el.classList.toggle("flashing", false), 1200);
          }
        };
      }
      r.push(
        makeButton(
          classes.join("."),
          s.showingDialog,
          action,
          text,
          legend,
          true,
          "move-mobile",
        )
      );
    }
    if (s.showForceResignMobile) {
      // Force resignation button (only shown on mobile,
      // and only if submit move button is not shown)
      const txt = ts("Þvinga til uppgjafar");
      r.push(
        makeButton(
          "force-resign",
          s.showingDialog,
          () => game.forceResign(), // Note: plain game.forceResign doesn't work here
          txt,
          txt,
        )
      );
    }
    if (s.showPass) {
      // Pass move: shown if no tiles have been placed
      // and we're not showing a dialog, or if this is
      // the last move by the opponent in a manual game
      const disabled = (s.tilesPlaced || s.showingDialog) && !s.lastChallenge;
      r.push(
        makeButton(
          "submitpass",
          disabled,
          () => game.submitPass(), // Note: plain game.submitPass doesn't work here
          ts("Pass"),
          glyph("forward"),
        )
      );
    }
    if (s.showExchange) {
      // Exchange tiles from the rack
      const disabled = !s.exchangeAllowed || s.tilesPlaced || s.showingDialog;
      r.push(
        makeButton(
          "submitexchange",
          disabled,
          () => game.submitExchange(), // Note: plain game.submitExchange doesn't work here
          ts("Skipta stöfum"),
          glyph("refresh"),
        )
      );
    }
    if (s.showResign) {
      // Resign the game
      r.push(
        makeButton(
          "submitresign", s.showingDialog,
          () => game.submitResign(), // Note: plain game.submitResign doesn't work here
          ts("Gefa viðureign"),
          glyph("fire"),
        )
      );
    }
    if (!s.gameOver && !s.localTurn && !game.moveInProgress && game.player !== null) {
      // Indicate that it is the opponent's turn; offer to force a resignation
      // if the opponent hasn't moved for 14 days
      r.push(
        m(".opp-turn",
          { style: { visibility: "visible" } },
          [
            m("span.move-indicator"),
            nbsp(),
            m("strong", game.nickname[1 - game.player]),
            ts(" á leik"),
            nbsp(),
            // The following inline button is only
            // displayed in the fullscreen UI
            s.tardyOpponent ? m("span.yesnobutton",
              {
                id: 'force-resign',
                onclick: (ev: Event) => {
                  ev.preventDefault();
                  game.forceResign();
                },
                onmouseout: buttonOut,
                onmouseover: buttonOver,
                title: ts("14 dagar liðnir án leiks")
              },
              ts("Þvinga til uppgjafar")
            ) : ""
          ]
        )
      );
    }
    if (s.tilesPlaced) {
      // Show the score of the current move (not visible on mobile)
      const sc = m(Score, { game });
      sc && r.push(sc);
    }
    // Is the server processing a move?
    if (game?.moveInProgress) {
      r.push(
        m(".waitmove",
          m(".animated-waitmove",
            m(AnimatedNetskraflLogo, {
              withCircle: false,
            })
          )
        )
      );
    }
    return m(".buttons", r);
  }
};
